<template>
  <div class="flex flex-column gap-1">
    <div v-if="usesCorrectedViscosityRate" class="flex justify-content-end">
      <Badge severity="danger">Uses corrected viscosity rate</Badge>
    </div>
    <div
      v-if="!hidePumpSelector"
      class="flex flex-column align-items-end gap-1"
    >
      <Dropdown
        v-model="selectedPump"
        optionLabel="label"
        :options="pumpOptions"
        append-to="body"
      />
    </div>
    <div
      class="single-pump-chart"
      :class="{ 'is-static': isStatic, 'is-horizontal': isHorizontal }"
    >
      <DDV
        v-slot="{
          enableMouseTracking,
          onUpdateNearestPoint,
          mouseState,
          margins
        }"
        v-bind="$attrs"
        class="pump-performance"
        :resize-throttle-ms="100"
        @update:dom-state="domState = $event"
      >
        <template v-if="domState">
          <rect
            v-if="domState.plotWidth > 0"
            :x="margins.left"
            :y="margins.top"
            :width="domState.plotWidth"
            :height="domState.plotHeight"
            fill="var(--plotSurface)"
          />
          <template
            v-if="
              producedTDHPoints.length &&
              brakeHPPoints.length &&
              efficiencyPoints.length
            "
          >
            <!-- TDH Curve -->
            <CartesianScaler
              v-if="domState.plotWidth > 0"
              v-slot="{ xScaler, yScaler }"
              :x-source="aggregateFlowRateDomain"
              :y-source="aggregateHeadDomain"
              :x-target="targetDomainX"
              :y-target="targetDomainY"
              reverse-y-axis
            >
              <Axis
                :x="margins.left"
                :y="margins.top"
                :domain="aggregateFlowRateDomain"
                :label="dataSetTDH?.xAxisLabel"
                :scaler="xScaler"
                :tick-frequency="ddvTickFrequencies?.x"
                draw-grid-lines
              />
              <Axis
                :x="margins.left"
                :y="margins.top"
                :domain="aggregateHeadDomain"
                :label="dataSetTDH?.yAxisLabel"
                :scaler="yScaler"
                :tick-frequency="ddvTickFrequencies?.y"
                is-vertical
                draw-grid-lines
              />
              <g :transform="`translate(${margins.left} ${margins.top})`">
                <LinePlot
                  :is-active="activeDataSets[0]"
                  :data-series-number="0"
                  :screen-points="screenPointsTDH"
                  curve-interpolator="catmull-rom"
                />
                <BSP
                  v-slot="{ nearestPoint }"
                  ignore-y-values
                  :points="dataSetTDH?.points"
                  :screen-points="screenPointsTDH"
                  :track-nearest-point="
                    enableMouseTracking && activeDataSets[0]
                  "
                  @update:nearest-point="
                    onUpdateNearestPoint(
                      dataSetTDH?.title ?? '',
                      0,
                      activeDataSets[0],
                      $event,
                      formatValue,
                      formatValue,
                      `(${targetXUnitLabel})`,
                      `(${targetYUnitLabel})`
                    )
                  "
                >
                  <template v-for="(t, i) in thresholds">
                    <template v-if="typeof t.value === 'number'">
                      <template
                        v-if="
                          aggregateFlowRateDomain.min < t.value &&
                          t.value < aggregateFlowRateDomain.max
                        "
                      >
                        <ThresholdLine
                          :key="i"
                          :is-x="true"
                          :scaler="xScaler"
                          :domain="aggregateFlowRateDomain"
                          :label="t.label"
                          :value="t.value"
                          :color="t.color"
                          :nearest-point="nearestPoint"
                        />
                      </template>
                    </template>
                    <template v-else-if="'min' in t.value">
                      <ThresholdArea
                        v-if="activeThresholds[i]"
                        :key="i"
                        :is-x="true"
                        :scaler="xScaler"
                        :label="t.label"
                        :domain="aggregateFlowRateDomain"
                        :min="
                          clamp(t.value.min, 0, aggregateFlowRateDomain.max)
                        "
                        :max="
                          clamp(t.value.max, 0, aggregateFlowRateDomain.max)
                        "
                        :color="t.color"
                        :nearest-point="nearestPoint"
                        :pattern-thickness="1.5"
                      />
                    </template>
                  </template>

                  <NearestPoint
                    v-if="nearestPoint?.node"
                    :data-series-number="0"
                    :nearest-point="nearestPoint"
                  />
                </BSP>
              </g>
            </CartesianScaler>

            <!-- Req TDH Curve -->
            <CartesianScaler
              v-if="domState.plotWidth > 0 && requiredTDHPoints.length > 0"
              v-slot="{ xScaler, yScaler }"
              :x-source="aggregateFlowRateDomain"
              :y-source="aggregateHeadDomain"
              :x-target="targetDomainX"
              :y-target="targetDomainY"
              reverse-y-axis
            >
              <g :transform="`translate(${margins.left} ${margins.top})`">
                <LinePlot
                  :is-active="activeDataSets[3]"
                  :data-series-number="3"
                  :screen-points="screenPointsRequiredTDH"
                  curve-interpolator="straight"
                />
                <BSP
                  v-slot="{ nearestPoint }"
                  ignore-y-values
                  :points="dataSetRequiredTDH?.points"
                  :screen-points="screenPointsRequiredTDH"
                  :track-nearest-point="
                    enableMouseTracking && activeDataSets[3]
                  "
                  @update:nearest-point="
                    onUpdateNearestPoint(
                      dataSetRequiredTDH?.title ?? '',
                      3,
                      activeDataSets[3],
                      $event,
                      formatValue,
                      formatValue,
                      `(${targetXUnitLabel})`,
                      `(${targetYUnitLabel})`
                    )
                  "
                >
                  <NearestPoint
                    v-if="nearestPoint?.node"
                    :data-series-number="3"
                    :nearest-point="nearestPoint"
                  />
                </BSP>
                <g
                  v-if="
                    activeDataSets[3] && hpsRequiredTDHOperatingPoint?.point
                  "
                  :key="hpsRequiredTDHOperatingPoint?.label"
                  :transform="`
                translate(
                  ${xScaler.scale(hpsRequiredTDHOperatingPoint?.point.x)},
                  ${yScaler.scale(hpsRequiredTDHOperatingPoint?.point.y)}
                )
              `"
                >
                  <g>
                    <circle
                      :cx="0"
                      :cy="0"
                      :r="5"
                      :stroke="hpsRequiredTDHOperatingPoint?.color"
                      :stroke-width="2"
                      fill="transparent"
                    />
                    <circle
                      :cx="0"
                      :cy="0"
                      :r="5"
                      :stroke="hpsRequiredTDHOperatingPoint?.color"
                      :stroke-opacity="0.3"
                      :stroke-width="8"
                      fill="transparent"
                    />
                    <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
                  </g>
                </g>
              </g>
            </CartesianScaler>

            <!-- BHP Curve -->
            <Axis
              ref="horsepowerYAxis"
              :label="dataSetHorsepower?.yAxisLabel"
              :domain="horsepowerDomain"
              :x="margins.left + domState.plotWidth"
              :y="margins.top"
              :scaler="horsePowerScaler"
              :tick-frequency="ddvTickFrequencies?.y"
              is-vertical
              is-right
            />

            <g :transform="`translate(${margins.left} ${margins.top})`">
              <BSP
                v-slot="{ screenPoints, nearestPoint }"
                ignore-y-values
                :points="dataSetHorsepower?.points"
                :screen-points="screenPointsHorsePower"
                :track-nearest-point="enableMouseTracking && activeDataSets[1]"
                @update:nearest-point="
                  onUpdateNearestPoint(
                    dataSetHorsepower?.title ?? '',
                    1,
                    activeDataSets[1],
                    $event,
                    formatValue,
                    formatValue,
                    `(${targetXUnitLabel})`,
                    '(bhp)'
                  )
                "
              >
                <LinePlot
                  :is-active="activeDataSets[1]"
                  :data-series-number="1"
                  :x="margins.left"
                  :y="margins.top"
                  :screen-points="screenPoints"
                  curve-interpolator="catmull-rom"
                />

                <NearestPoint
                  v-if="nearestPoint?.node"
                  :data-series-number="1"
                  :nearest-point="nearestPoint"
                />
              </BSP>
            </g>

            <!-- Efficiency Curve -->
            <Axis
              :label="dataSetEfficiency?.yAxisLabel"
              :domain="efficiencyDomain"
              :x="margins.left + domState.plotWidth + efficiencyYAxisOffset"
              :y="margins.top"
              :scaler="efficiencyScaler"
              :plot-margin-px="10"
              :tick-frequency="ddvTickFrequencies?.y"
              is-vertical
              is-right
            />

            <g :transform="`translate(${margins.left} ${margins.top})`">
              <BSP
                v-slot="{ screenPoints, nearestPoint }"
                ignore-y-values
                :points="dataSetEfficiency?.points"
                :screen-points="screenPointsEfficiency"
                :track-nearest-point="enableMouseTracking && activeDataSets[2]"
                @update:nearest-point="
                  onUpdateNearestPoint(
                    dataSetEfficiency?.title ?? '',
                    2,
                    activeDataSets[2],
                    $event,
                    formatValue,
                    formatValue,
                    `(${targetXUnitLabel})`,
                    '(%)'
                  )
                "
              >
                <LinePlot
                  :is-active="activeDataSets[2]"
                  :data-series-number="2"
                  :x="margins.left"
                  :y="margins.top"
                  :screen-points="screenPoints"
                  curve-interpolator="catmull-rom"
                />

                <NearestPoint
                  v-if="nearestPoint?.node"
                  :data-series-number="2"
                  :nearest-point="nearestPoint"
                />
              </BSP>
            </g>
          </template>
          <template v-else>
            <text
              fill="var(--plotColor0)"
              x="50%"
              y="50%"
              dominant-baseline="middle"
              text-anchor="middle"
            >
              No Data
            </text>
          </template>

          <ChartTitle :title="title" />

          <Crosshairs
            v-if="readyToDraw(enableMouseTracking, mouseState.isHovered)"
            ignore-y
            :x="margins.left"
            :y="margins.top"
          />
        </template>
      </DDV>

      <div>
        <CustomLegend
          v-if="drawLegend"
          v-model="activeDataSets"
          :model="SinglePumpDataSets"
          :static="isStatic"
          :horizontal="isHorizontal"
        />
        <CustomLegend
          v-if="drawLegend"
          v-model="activeThresholds"
          :model="thresholds"
          :horizontal="isHorizontal"
          static
        />
      </div>
      <div>
        <div v-if="displayNpshToggle" class="flex justify-content-end">
          <LabelledToggle v-model="showNPSHChart" :text="`NPSH`" />
        </div>
        <div
          v-if="showNPSHChart"
          class="single-pump-chart"
          :class="{ 'is-static': isStatic, 'is-horizontal': isHorizontal }"
        >
          <SinglePumpNPSHChart
            v-bind="$attrs"
            :chart-data="chartingData"
            :draw-legend="drawLegend"
            :is-static="isStatic"
            :is-horizontal="isHorizontal"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import type {
  DataSet,
  DOMState,
  NumericPoint,
  Threshold
} from "@incytestudios/ddv/types/common";
import CustomLegend from "./CustomLegend.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";

// Eslint auto fixing is causing issues with this import
// eslint-disable-next-line
import {
  Axis,
  fromRange,
  DDV,
  BSP,
  LinePlot,
  ChartTitle,
  ThresholdArea,
  ThresholdLine,
  Crosshairs,
  NearestPoint,
  CartesianScaler,
  getDefaultValueFormatter,
  useScreenPoints,
  useLinearScaler
} from "@incytestudios/ddv";

import type {
  Correlations,
  DeviationSurvey,
  Equipment,
  OperatingParameters,
  Scenario,
  WellProperties,
  XrefCables,
  XrefHousings,
  XrefIntakes,
  XrefPumps,
  XrefSeals
} from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { NumericDomain } from "@incytestudios/ddv/types/domain";
import { clamp } from "@vueuse/core";
import { useSinglePumpChartData } from "@/libs/charting/single/useSinglePumpChartData";
import {
  scenarioKey,
  sizingKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import type { OperatingPoint } from "@/components/types";
import type { SinglePumpChartRequiredTDHDataSet } from "@/libs/charting";
import SinglePumpNPSHChart from "./SinglePumpNPSHChart.vue";
import { HorsePower } from "@/libs/units/power";

const props = defineProps<{
  title?: string;
  scenario?: Scenario | null;
  scenarioCalculations?: ScenarioCalculations;
  equipment?: Equipment;
  parameters?: OperatingParameters;
  wellProperties?: WellProperties;
  correlations?: Correlations;
  pumps?: Record<string, XrefPumps[number]>;
  seals?: Record<string, XrefSeals[number]>;
  intakes?: Record<string, XrefIntakes[number]>;
  housings?: Record<string, XrefHousings[number]>;
  cables?: Record<string, XrefCables[number]>;
  deviationSurvey?: DeviationSurvey;
  drawLegend?: boolean;
  isHorizontal?: boolean;
  isStatic?: boolean;
  hidePumpSelector?: boolean;
  npshMode?: boolean;
  displayNpshToggle?: boolean;
}>();

const _scenario = props.scenario
  ? ref<Scenario>(props.scenario)
  : injectStrict(scenarioKey);
const _sizing = injectStrict(sizingKey, null);
const _calcs =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);

const pumpOptions = ref(
  _calcs.descendingOrderPumps.value.map(p => {
    return {
      label: `${p.r_pump?.series_model}${_calcs.primaryPump.value?.id === p.id ? " (primary)" : ""}`,
      pump: p
    };
  })
);
// Selected pump will always be primary pump unless
// we allow the user to choose which pump they want again
const selectedPump = ref(pumpOptions.value[0]);

const { getLiquidFlowUnit, getLargeLengthUnit, getPressureUnit } =
  useScenarioUnits(_scenario, _sizing);

// The override keys used here will hopefully go away if we add unit controls
// specifically for the pump table/charts
const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit("Pump Intake Pressure.convert.PSIA");

// shim DDV type because it isn't exported
type ThresholdWithDomain = Threshold & {
  value: {
    min: number;
    max: number;
  };
};

type ThresholdWithNumber = Threshold & {
  value: number;
};

const showNPSHChart = ref(props.npshMode ?? true);

const readyToDraw = (enableMouseTracking: boolean, isHovered: boolean) => {
  return (
    enableMouseTracking &&
    isHovered &&
    producedTDHPoints.value.length &&
    requiredTDHPoints.value.length &&
    brakeHPPoints.value.length &&
    efficiencyPoints.value.length
  );
};

const domState = ref<DOMState>();

const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);

// DATA SECTION - things directly derived from new refactored calcs.
//
// This chart is named single pump chart, but really it shows
// the whole pump stack working together and mainly uses the
// primary pump data
// Stuff above here references these values to reactively
// control the chart - and has not been touched.
const chartingData = computed(() =>
  useSinglePumpChartData(
    _calcs,
    targetLiquidFlowUnit.value,
    targetLargeLengthUnit.value,
    targetPressureUnit.value,
    // For now, this is hardcoded since BHP is always in HorsePower for now
    HorsePower
  )
);

// X unit will be liquid flow rate for this chart
const targetXUnitLabel = computed(() => {
  return chartingData.value?.targetXUnitLabel;
});

const targetYUnitLabel = computed(() => {
  return chartingData.value?.targetYUnitLabel;
});

const dataSetTDH = computed<DataSet | undefined>(() => {
  return chartingData.value?.dataSets.tdhProduced;
});
const dataSetRequiredTDH = computed<
  SinglePumpChartRequiredTDHDataSet | undefined
>(() => {
  return chartingData.value?.dataSets.tdhRequired;
});
const dataSetHorsepower = computed<DataSet | undefined>(() => {
  return chartingData.value?.dataSets.bhp;
});
const dataSetEfficiency = computed<DataSet | undefined>(() => {
  return chartingData.value?.dataSets.efficiency;
});

const usesCorrectedViscosityRate = computed(
  () => chartingData.value?.isRateCorrected
);

const SinglePumpDataSets = computed(() => {
  if (
    !dataSetTDH.value ||
    !dataSetHorsepower.value ||
    !dataSetEfficiency.value ||
    !dataSetRequiredTDH.value
  ) {
    return undefined;
  } else {
    return [
      dataSetTDH.value,
      dataSetHorsepower.value,
      dataSetEfficiency.value,
      dataSetRequiredTDH.value
    ];
  }
});

// This is charted for HPS only scenarios instead of dataSetRequiredTDH
const hpsRequiredTDHOperatingPoint = computed<OperatingPoint | null>(() => {
  // plotColor3 was used since that is the color used for required TDH line
  // up inside the template chart code
  if (dataSetRequiredTDH.value?.hpsRequiredTDHPoint) {
    return {
      label: "HPS Required TDH Operating Point",
      color: "var(--plotColor3)",
      point: dataSetRequiredTDH.value?.hpsRequiredTDHPoint
    };
  }
  return null;
});

const requiredTDHPoints = computed<NumericPoint[]>(() => {
  return (dataSetRequiredTDH.value?.points || []) as NumericPoint[];
});

const producedTDHPoints = computed<NumericPoint[]>(() => {
  return (dataSetTDH.value?.points || []) as NumericPoint[];
});

const brakeHPPoints = computed<NumericPoint[]>(() => {
  return (dataSetHorsepower.value?.points || []) as NumericPoint[];
});

const efficiencyPoints = computed<NumericPoint[]>(() => {
  return (dataSetEfficiency.value?.points || []) as NumericPoint[];
});

const rorThreshold = computed<ThresholdWithDomain>(() => {
  const min = chartingData.value?.rorMin || 0;
  const max = chartingData.value?.rorMax || 0;

  // Convert from standard units (STB/D) to target unit
  return {
    label: `Operating Range (${targetXUnitLabel.value})`,
    color: "var(--plotColor4)",
    value: {
      min,
      max
    }
  };
});

const bepThreshold = computed<ThresholdWithNumber>(() => {
  const bepValue = chartingData.value?.rorBep || 0;

  return {
    label: `Best Efficiency (${targetXUnitLabel.value})`,
    color: "var(--plotColor5)",
    value: bepValue
  };
});

// operatingPointThreshold is the pump discharge flow rate
const operatingPointThreshold = computed<ThresholdWithNumber>(() => {
  const opPoint = chartingData.value?.operatingPointThreshold || 0;

  return {
    label: `Q @ Discharge (${targetXUnitLabel.value})`,
    color: "var(--plotColor6)",
    value: opPoint
  };
});

const thresholds = computed(() => [
  bepThreshold.value,
  operatingPointThreshold.value,
  rorThreshold.value
]);

const activeThresholds = ref(thresholds.value.map(() => true));

const targetDomainX = computed(() => fromRange(domState.value?.plotWidth || 0));
const targetDomainY = computed(() =>
  fromRange(domState.value?.plotHeight || 0)
);

const formatValue = getDefaultValueFormatter();

const horsepowerYAxis = ref<typeof Axis | null>(null);

const efficiencyYAxisOffset = computed(() => {
  return horsepowerYAxis.value?.$el
    ? horsepowerYAxis.value.$el.getBoundingClientRect().width
    : 0;
});

const activeDataSets = ref([true, true, true, true]);

const aggregateFlowRateDomain = computed<NumericDomain>(() => ({
  min: 0,
  max:
    Math.max(
      ...(dataSetTDH.value?.points || []).map(({ x }) =>
        typeof x === "number" ? x : 0
      ),
      ...(dataSetRequiredTDH.value?.points || []).map(({ x }) =>
        typeof x === "number" ? x : 0
      ),
      ...(dataSetHorsepower.value?.points || []).map(({ x }) =>
        typeof x === "number" ? x : 0
      )
    ) * 1.1
}));

const horsepowerDomain = computed<NumericDomain>(() => ({
  min: 0,
  max: brakeHPPoints.value
    ? Math.max(...brakeHPPoints.value.map(p => p.y)) * 1.1
    : 0
}));

const efficiencyDomain = computed<NumericDomain>(() => {
  // Should usually be 0-100%, but allow for some headroom if
  // efficiency goes above 100% if we adjust for surface rates
  const maxY = Math.max(...efficiencyPoints.value.map(p => p.y));
  return {
    min: 0,
    max: maxY < 100 ? 100 : maxY * 1.1
  };
});

const aggregateHeadDomain = computed<NumericDomain>(() => ({
  min: 0,
  max:
    Math.max(
      ...(dataSetTDH.value?.points || []).map(({ y }) =>
        typeof y === "number" ? y : 0
      ),
      ...(dataSetRequiredTDH.value?.points || []).map(({ y }) =>
        typeof y === "number" ? y : 0
      )
    ) * 1.1
}));

const flowRateScaler = useLinearScaler(
  aggregateFlowRateDomain,
  targetDomainX,
  false
);
const headScaler = useLinearScaler(aggregateHeadDomain, targetDomainY, true);
const horsePowerScaler = useLinearScaler(horsepowerDomain, targetDomainY, true);
const efficiencyScaler = useLinearScaler(efficiencyDomain, targetDomainY, true);

const { screenPoints: screenPointsTDH } = useScreenPoints(
  producedTDHPoints,
  flowRateScaler,
  headScaler
);
const { screenPoints: screenPointsRequiredTDH } = useScreenPoints(
  requiredTDHPoints,
  flowRateScaler,
  headScaler
);
const { screenPoints: screenPointsEfficiency } = useScreenPoints(
  efficiencyPoints,
  flowRateScaler,
  efficiencyScaler
);
const { screenPoints: screenPointsHorsePower } = useScreenPoints(
  brakeHPPoints,
  flowRateScaler,
  horsePowerScaler
);
</script>

<style lang="scss" scoped>
.single-pump-chart {
  display: grid;
  grid-template-columns: 3fr max-content;

  &.is-horizontal {
    grid-template-columns: 1fr;
  }

  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }
}

.pump-performance {
  :deep(.threshold) {
    line {
      stroke-dasharray: 10px 4px;
    }
  }
}

@media screen and (max-width: 870px) {
  .single-pump-chart {
    grid-template-columns: 1fr;
  }
}
</style>
